/** Number and date formatting. Only <Metric> (and chart axes) call the number formatters. */
import type { Readiness } from '../api/types';

/**
 * Window readiness, identical to @studio/core `windowReadiness` (parity is unit-tested). Re-implemented here
 * because importing @studio/core's entry point pulls zod into the browser bundle.
 */
export function windowReadiness(
  windowDays: number,
  asOf: string | null,
  historyStart: string | null,
): Readiness {
  if (asOf === null || historyStart === null || historyStart > asOf) {
    return { status: 'collecting', have: 0, need: windowDays };
  }
  const span = Math.round(
    (Date.parse(`${asOf}T00:00:00Z`) - Date.parse(`${historyStart}T00:00:00Z`)) / 86_400_000,
  );
  const have = Math.min(windowDays, span + 1);
  return have >= windowDays ? { status: 'ready' } : { status: 'collecting', have, need: windowDays };
}

/**
 * `windowReadiness` returns `have: 0` when history_start is null (the first catalog pass, 95% of listed islands
 * fetched once, hasn't finished), when as_of is null (no snapshot published yet), or when history_start > as_of
 * (unreachable with real data). Saying "0 of 7 days stored" then is false, so the UI names the pass instead.
 * Never build a stand-in `have: 0` on the client while data is loading; it would read as "first pass".
 */
export function isFirstPass(r: Readiness): boolean {
  return r.status === 'collecting' && r.have === 0;
}

/** Short pill text for a collecting window: "first pass" or "collecting (3/7)". */
export function collectingPill(r: Readiness & { status: 'collecting' }): string {
  return isFirstPass(r) ? 'first pass' : `collecting (${r.have}/${r.need})`;
}

export type NumberFormat =
  | 'compact'
  | 'int'
  | 'decimal'
  | 'pct'
  | 'signedPct'
  | 'score'
  | 'usd'
  | 'similarity'
  | 'cited';

/** 1.2M, 18k, 4.9k, 950 — matches the mockup's `fmt`. */
export function compact(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e4) return `${Math.round(n / 1e3)}k`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
}

export function formatNumber(n: number, f: NumberFormat): string {
  switch (f) {
    case 'compact':
      return compact(n);
    case 'int':
      return Math.round(n).toLocaleString('en-US');
    case 'decimal':
      return n.toFixed(1);
    case 'pct':
      // Ratios in [0,1] (retention, shares).
      return `${Math.round(n * 100)}%`;
    case 'signedPct':
      // Already a percentage (wow_pct, growth_pct).
      return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(Math.round(n))}%`;
    case 'score':
      return String(Math.round(n));
    case 'cited':
      // A number a concept cites: large or whole numbers grouped, small fractions to 3 significant digits
      // (0.744, 75.7) so the chip matches the concept text.
      return Number.isInteger(n) || Math.abs(n) >= 1000
        ? Math.round(n).toLocaleString('en-US')
        : String(Number(n.toPrecision(3)));
    case 'usd':
      return `$${n.toFixed(2)}`;
    case 'similarity':
      return n.toFixed(2);
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-05" → "5 Oct 2026" (UTC day, no timezone shift). */
export function formatDay(day: string | null | undefined, opts: { year?: boolean } = {}): string {
  if (!day) return '—';
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return day;
  const base = `${d} ${MONTHS[m - 1]}`;
  return opts.year === false ? base : `${base} ${y}`;
}

/** ISO timestamp → "06:02 UTC". */
export function formatUtcTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  return `${t.toISOString().slice(11, 16)} UTC`;
}

/** ISO timestamp → "5 Oct 09:17". */
export function formatUtcDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  return `${formatDay(t.toISOString().slice(0, 10), { year: false })} ${t.toISOString().slice(11, 16)}`;
}

export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function addDaysUtc(day: string, n: number): string {
  const t = new Date(`${day}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
}

/** Stale when as_of is older than today − 2 days (ANALYTICS.STALE_AFTER_DAYS). */
export function isStaleAsOf(asOf: string | null, today: string, staleAfterDays = 2): boolean {
  return asOf === null || asOf < addDaysUtc(today, -staleAfterDays);
}

/** "$0.029", "$6.40": a cost per call needs three decimals, spend two. */
export function formatUsd(n: number, digits = 2): string {
  return `$${n.toFixed(digits)}`;
}
